import type { BulkActionResult } from './index';

export type BulkItemOutcome = { ok: true } | { ok: false; reason: string };

/**
 * Apply one change to each selected item in turn and count what actually
 * happened. Items are written one at a time so a refusal on the fourth is
 * reported as such rather than hidden behind the twelve that went through,
 * and so no two writes race each other for the same row.
 */
export async function runBulk(
  action: string,
  ids: string[],
  titleOf: (id: string) => string,
  perItem: (id: string) => Promise<BulkItemOutcome>,
  onProgress?: (done: number, total: number) => void,
): Promise<BulkActionResult> {
  const result: BulkActionResult = { action, total: ids.length, succeeded: 0, succeededIds: [], failed: [] };
  let done = 0;
  for (const id of ids) {
    let outcome: BulkItemOutcome;
    try {
      outcome = await perItem(id);
    } catch (err) {
      outcome = { ok: false, reason: err instanceof Error ? err.message : 'Unknown error' };
    }
    if (outcome.ok) {
      result.succeeded += 1;
      result.succeededIds.push(id);
    } else {
      result.failed.push({ itemId: id, itemTitle: titleOf(id), reason: outcome.reason });
    }
    done += 1;
    onProgress?.(done, ids.length);
  }
  return result;
}
